import { C62 } from 'constants/weightOptions';
import { isDefined } from 'utils/validation';
import { getProductByLineNumber } from 'redux/modules/entities/selectors/products';
import { getIsFavourite } from 'redux/modules/favourites-products/selectors';
import { getCustomerOrderId } from 'redux/modules/sessions/selectors';
import { getAnalyticsTrolleyItems } from 'redux/modules/trolley/selectors/get-analytics-trolley-items';
import { getSponsorshipByLineNumber } from 'redux/modules/search-and-browse/selectors';
import {
  getOfferBuilderItems,
  getPromotionsByIds,
} from 'redux/modules/entities/selectors/promotions';
import { SEARCH_TYPE as MEAL_DEALS_SEARCH_TYPE } from 'components/MealDealPage/constants';
import { SEARCH_TYPE as OFFER_DETAILS_SEARCH_TYPE } from 'components/Offers/OfferDetails/constants';
import { offerBuilderDetails } from 'redux/middleware/analytics/actions-to-track/trolley/utils/offer-builder-details';
import {
  getMealDealGroupCompletionStatus,
  getMealDealGroups,
  getMealDealId,
  getTotalCompletedMealDeals,
} from 'redux/modules/meal-deals/selectors';
import getCompleteMealDealEvent from 'redux/middleware/analytics/actions-to-track/trolley/utils/complete-meal-deal';
import type {
  ChangeCartGTMEvent,
  ClickContext,
  Payload,
} from 'redux/middleware/analytics/actions-to-track/trolley/types';
import { getItemListFormatted, mapToImpression } from 'analytics/product-impressions-tracking';
import { getPricingAndOfferDetails } from 'hooks/use-pod-impression';
import { ADD_TO_TROLLEY_DESCRIPTION } from './constants';

const noPromo = { type: undefined, name: undefined };
const noPersonalisation = { type: undefined, name: undefined };

const getMealDealAnalytics = (state: WtrState, payload: Payload, method: string) => {
  const { lineNumber, searchType } = payload[0].analytics;

  if (searchType !== MEAL_DEALS_SEARCH_TYPE) {
    return {};
  }

  const gtmEventCompleteMealDealBuilder =
    method === 'add' ? getCompleteMealDealEvent(state, lineNumber) : undefined;

  const id = getMealDealId(state);
  const groups = getMealDealGroups(state);

  // `searchType` check guarantees we're on a meal deal page, and groups are
  // assured given a product has been added or removed.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const group = groups.find(({ lineNumbers }) => lineNumbers.includes(lineNumber))!;

  const mealDeal = {
    id,
    line_id: `${group.name} (ID: ${group.id})`,
    total_complete: getTotalCompletedMealDeals(state),
    incomplete_status: getMealDealGroupCompletionStatus(state),
  };

  return { mealDeal, gtmEventCompleteMealDealBuilder };
};

const getOfferBuilderAnalytics = (state: WtrState, payload: Payload, method: string) => {
  const { searchType, lineNumber } = payload[0].analytics;

  if (searchType !== OFFER_DETAILS_SEARCH_TYPE) {
    return {};
  }

  // @ts-expect-error entities state is not typed
  const product = getProductByLineNumber(state, lineNumber);
  const promotionId = product?.promotion?.promotionId;

  if (!promotionId) {
    return {};
  }

  const builders = getOfferBuilderItems(state, `${promotionId}`);

  const { offerId, totalComplete, completedStatus, completionStatus, offerType } =
    offerBuilderDetails({ builders }) || {};

  if (!offerId) {
    return {};
  }

  const buildYourOffer = {
    id: offerId,
    total_complete: totalComplete,
    completion_status: completedStatus ? 'complete' : 'incomplete',
    offer_type: offerType,
  };

  const gtmEventCompleteOfferBuilder = completionStatus
    ? {
        event: 'complete_build_your_offer',
        build_your_offer: {
          id: offerId,
          offer_type: offerType,
        },
        method:
          method === 'add' ? 'offer completed by adding item' : 'offer completed by removing item',
      }
    : null;

  return { buildYourOffer, gtmEventCompleteOfferBuilder };
};

/**
 * Used by all trolley transforms so that the output event data shares the same schema,
 * differing only in the event type and the ecommerce method key (`add` or `remove`),
 * with `click_source: "add_all"` being included at the root of the object for the
 * "Add all X items to trolley" feature.
 *
 * All payload items are reported in a single event, with the ecommerce-level context
 * (e.g. item list, promotion, personalisation) taken from the first item.
 *
 * Example object to be pushed to GTM data layer with the `add` method:
 *
 * ```json
 * {
 *   event: 'add_to_cart',
 *   ecommerce: {
 *     currencyCode: 'GBP',
 *     add: {
 *       products: [{
 *         'name': 'product name',
 *         'id': '174535',
 *         'price': '1.23',
 *         'quantity': 123
 *         'brand': undefined,
 *         'category': undefined,
 *         'position': undefined,
 *         'favourite': undefined,
 *         'offer': undefined,
 *       }]
 *     }
 *   }
 * }
 * ```
 *
 * Any property with `undefined` value represents information that (at the moment)
 * cannot be extracted from the store as not being provided by the back-end.
 */
const changeCartTransform = (state: WtrState, payload: Payload, method: string, event: string) => {
  const orderId = getCustomerOrderId(state);

  const mutations = payload.map(({ analytics }) => {
    const { lineNumber, uom, productPosition, clickContext, productFindingMethod } = analytics;
    const amendHubGA = analytics?.clickContext?.productFindingMethod;
    // @ts-expect-error entities state is not typed
    const product = getProductByLineNumber(state, lineNumber);
    const { sponsored, sponsorshipId } = getSponsorshipByLineNumber(state, lineNumber);
    const { brand, categories = [], name, productType, promotions: promotionIds } = product || {};
    const isWeighted = uom !== C62;
    const { prevQuantity: quantityBefore = 0, quantity: quantityAfter = 0 } =
      getAnalyticsTrolleyItems(state, lineNumber) ?? {};
    const quantityDelta = Math.abs(quantityAfter - quantityBefore);
    const quantity = isWeighted ? 1 : quantityDelta;

    const customerFavourite = getIsFavourite(state, lineNumber);
    const categoriesNames = categories
      ? categories.map(({ name: categoryName }: { name: string }) => categoryName)
      : undefined;
    // @ts-expect-error favourites-products state is not typed
    const promotions = getPromotionsByIds(state, promotionIds || []);
    const { currencyCode, discount, offer, offerId, price } = getPricingAndOfferDetails(
      product,
      promotions,
    );
    const { listDefinitions, podType, availability } = clickContext || {};

    const impression = mapToImpression({
      availability: availability || 'available',
      brand,
      position: productPosition,
      currencyCode,
      id: product?.id,
      podType,
      lineNumber,
      name,
      offer: offer ?? '',
      price,
      quantity,
      listDefinitions,
      productFindingMethod,
      sponsored,
      sponsorshipId,
      uom: uom ?? '',
      categoriesNames,
      discount,
      offerId,
      productType,
      isFavourite: isDefined(customerFavourite) ? customerFavourite : undefined,
    });

    const forAddContext: { promotion?: ClickContext; personalisation?: ClickContext } = {};
    if (event === ADD_TO_TROLLEY_DESCRIPTION) {
      const { aemPromotion, personalisation } = clickContext || {};
      const sponsorship = sponsored ? { type: 'Sponsored Product', name: undefined } : undefined;
      forAddContext.promotion = aemPromotion || sponsorship || noPromo;
      forAddContext.personalisation = personalisation || noPersonalisation;
    }

    return {
      context: {
        ...getItemListFormatted(listDefinitions, amendHubGA || productFindingMethod),
        ...forAddContext,
        currencyCode,
      },
      impression,
      purchaseOrAmend: amendHubGA ? 'amend' : 'purchase',
      value: price * quantity,
    };
  });

  const [{ context, purchaseOrAmend }] = mutations;
  const value = mutations.reduce((total, change) => total + change.value, 0);

  const events: ChangeCartGTMEvent[] = [
    {
      _clear: true,
      event,
      ecommerce: {
        ...context,
        items: mutations.map(({ impression }) => impression),
        value: parseFloat(value.toFixed(2)),
        order_id: orderId,
        purchase_or_amend: purchaseOrAmend,
      },
    },
  ];

  const { gtmEventCompleteMealDealBuilder } = getMealDealAnalytics(state, payload, method);
  const { gtmEventCompleteOfferBuilder } = getOfferBuilderAnalytics(state, payload, method);

  if (gtmEventCompleteMealDealBuilder) {
    events.push(gtmEventCompleteMealDealBuilder);
  } else if (gtmEventCompleteOfferBuilder) {
    events.push(gtmEventCompleteOfferBuilder);
  }

  return events;
};

export default changeCartTransform;
